import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "@/features/axiosHelper";

import {
  Container,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  FormHelperText,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import ShepButton from "@/components/GenericComponents/ShepButton";
import ShepFormField from "@/components/GenericComponents/ShepFormField";
import { createAlert } from "@/features/homepage/alertSliceList";

import {
  endpointTypesEnum,
  appTypesEnum,
} from "@/features/connections/integrationSlice";
import "./NewEndpointSetup.css";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchUserRole, selectUserRole } from "@/features/auth/userSlice";
import { UserRoles } from "@/utils/utils";
import { InfoIcon } from "@/ImageRepo";
import ShepTooltip from "@/components/GenericComponents/ShepTooltip";

const defaultFtpInfo = {
  hostName: "",
  userName: "",
  password: "",
  downloadPath: "",
  uploadPath: "",
  downloadFileHandleMethod: "",
  connectMode: "",
  port: "",
  connectionType: "",
  mailboxName: "",
  ftpCommands: "",
  largeFileSize: "3000"
}
// Add/ Edit popup content component
const NewEndpointSetup = (props) => {
  const dispatch = useDispatch();
  const userRole = useSelector(selectUserRole);

  const UNCHANGED_PASSWORD = "********";

  // State variables set from api requests
  const [appTypes, setAppTypes] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [ftpConnectModes, setFtpConnectModes] = useState([]);
  const [sftpConnectModes, setSftpConnectModes] = useState([]);
  const [downloadFileTypes, setDownloadFileTypes] = useState([]);
  const [connectTypes, setConnectTypes] = useState([]);
  const [connectorTypes, setConnectorTypes] = useState([]);
  const [endpointEmail, setEndpointEmail] = useState({});
  const [generateEmailDisabled, setGenerateEmailDisabled] = useState(true);
  const [endpointPrompt, setEndpointPrompt] = useState("");

  // State variables filled out by user
  const [endpointName, setEndpointName] = useState("");
  const [selectedAppType, setSelectedAppType] = useState("");
  const [selectedProtocol, setSelectedProtocol] = useState("");
  const [selectedEndpoint, setSelectedEndpoint] = useState(
    props.selectedEndpoint,
  );
  const [selectedEndpointId, setSelectedEndpointId] = useState(
    props.selectedEndpointId,
  );

  // State variables to track save and edit statuses for close alert
  const [save, setSave] = useState(false);
  const [edited, setEdited] = useState(false);

  // SHEP-360 error validation state management and helper text
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState("");

  // initially set all form fields to be readonly so the saved values from browsers do not populate the form
  const [readOnly, setReadOnly] = useState(true);
  // State to track which fields are currently toggled to "visible"
  // It will look like: { apiConsumerKey: true, apiConsumerSecret: false }
  const [visiblePasswords, setVisiblePasswords] = useState({});

  // Helper function to flip the boolean for a specific field name
  const togglePasswordVisibility = (fieldName) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  // SHEP-731, prevent out of date tenants from using new Endpoint types
  const [requiresUpdate, setRequiresUpdate] = useState(false);
  // parameters for ftp (SFTP, FTPS endpoint types)
  const [ftpInfo, setFtpInfo] = useState(defaultFtpInfo);

  const [largeFileSizeHasError, setLargeFileSizeHasError] = useState(false);

  const handleFtpInfoChange = (e) => {
    const { name, value } = e.target;
    setEdited(true);
    setFtpInfo({
      ...ftpInfo,
      [name]: value,
    });
  };

  const validateAndHandleFtpInfoNumberChange = (e, setErrorState) => {
    const { value } = e.target;
    if (value?.includes('.') || value?.includes('e') || isNaN(value) || Number(value) <= 0) {
      setErrorState(true);
    } else {
      setErrorState(false);
    }
    handleFtpInfoChange(e);
  }

  // parameters for WebAPI (RestAPI endpoint types)
  const [restApiInfo, setRestApiInfo] = useState({
    connectorType: "",
    serverName: "",
    userName: "",
    password: "",
    databaseCode: "",
    apiServerName: "",
    apiCompanyCode: "",
    apiConsumerKey: "",
    apiConsumerSecret: "",
    apiTokenId: "",
    apiTokenSecret: "",
    apiDatabaseCompanyCode: "",
  });

  const handleRestApiInfoChange = (e) => {
    const { name, value } = e.target;
    setEdited(true);
    setRestApiInfo({
      ...restApiInfo,
      [name]: value,
    });
  };

  const handleRestApiInfoClear = () => {
    const clearedInfo = {};
    Object.keys(restApiInfo).forEach((key) => {
      clearedInfo[key] = "";
    });
    setRestApiInfo(clearedInfo);
  };

  const [curlCommand, setCurlCommand] = useState("");
  const [showCurlBox, setShowCurlBox] = useState(false);
  const [curlLoading, setCurlLoading] = useState(false);

  useEffect(() => {
    getApptypes();
    getEndpointTypes();
    getFtpConnectModes();
    getSftpConnectModes();
    getDownloadFileTypes();
    getConnectTypes();
    getEmailEndpointAddress();
    dispatch(fetchUserRole());
  }, []);

  // Fill in values if editing existing endpoint
  useEffect(() => {
    if (Object.keys(selectedEndpoint).length) {
      prefillFormValues();
    } else {
      clearValues();
    }
  }, [selectedEndpoint]);

  // Automatically select the app type when endpoint type selection switches
  useEffect(() => {
    getApptypes();
    setRequiresUpdate(
      protocols.find((x) => x.Value === selectedProtocol)?.RequiresDBUpdate ??
        false,
    );
  }, [selectedProtocol]);

  useEffect(() => {
    if (selectedAppType || selectedAppType === 0) {
      getConnectorTypes(selectedAppType);
    }
  }, [selectedAppType]);

  useEffect(() => {
    setGenerateEmailDisabled(!!endpointEmail?.Email);
  }, [endpointEmail]);

  // Reset/close cURL area whenever any input field in restApiInfo changes
  useEffect(() => {
    setShowCurlBox(false);
    setCurlCommand("");
  }, [restApiInfo]);

  // Reset form values
  const clearValues = () => {
    setEndpointName("");
    setSelectedEndpointId(-1);
    setSelectedAppType("");
    setSelectedProtocol("");
    setFtpInfo(defaultFtpInfo);
    handleRestApiInfoClear();
  };

  // endpoint types/ protocol currently supported
  const endpointTypesToKeep = [
    endpointTypesEnum.SFTP,
    endpointTypesEnum.FTPS,
    endpointTypesEnum.RESTAPI,
    endpointTypesEnum.VirtualPacking,
    endpointTypesEnum.Email,
  ];

  // app types supported for each endpoint type
  const ftpAppTypes = [appTypesEnum.RetailChannel, appTypesEnum.WMS]; // default value selected for SFTP, FTPS
  const restApiAppTypes = [
    appTypesEnum.ERP, // default value to be selected
    appTypesEnum.RetailChannel,
    appTypesEnum.WMS, // SHEP-934: Now with the WMS SDK Connector, we support WMS with RestApi
    appTypesEnum.CMS,
  ];
  const virtualPackAppTypes = [appTypesEnum.WMS];
  const emailAppTypes = [appTypesEnum.RetailChannel];

  /**
   * Reduces the array to only keep elements that have matching values in `paramsToKeep`
   * @param {*} array Original array with all elements
   * @param {*} paramsToKeep Array of values to keep
   * @returns
   */
  const reduceArray = (array, paramsToKeep) => {
    var reducedArray = [];
    array.forEach((type) => {
      if (paramsToKeep.includes(type.Value)) {
        reducedArray.push(type);
      }
    });
    return reducedArray;
  };

  // Get requests for setting endpoint values (app, endpoint, connector types, connect modes, download types)
  /**
   * Returns and sets the available app types associated for each endpoint type.
   * Sets default value for selectedAppType to be 'RetailChannel' for ftp types and 'ERP' for rest api type
   */
  const getApptypes = () => {
    axios
      .get("/endpoint/app-types")
      .then((response) => {
        var result = [];
        if (
          selectedProtocol === endpointTypesEnum.FTPS ||
          selectedProtocol === endpointTypesEnum.SFTP
        ) {
          result = reduceArray(response.data, ftpAppTypes);
        } else if (selectedProtocol === endpointTypesEnum.RESTAPI) {
          result = reduceArray(response.data, restApiAppTypes);
        } else if (selectedProtocol === endpointTypesEnum.VirtualPacking) {
          result = reduceArray(response.data, virtualPackAppTypes);
        } else if (selectedProtocol === endpointTypesEnum.Email) {
          result = reduceArray(response.data, emailAppTypes);
        }
        let appType =
          result.find((item) => item.Value === selectedEndpoint.AppType)
            ?.Value ?? "";
        setSelectedAppType(appType);
        setAppTypes(result);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  /**
   * Get endpoint types (protocols). Currently supports SFTP, FTPS, Rest API
   */
  const getEndpointTypes = () => {
    axios
      .get("/endpoint/protocols")
      .then((response) => {
        setProtocols(reduceArray(response.data, endpointTypesToKeep));
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  const getConnectorTypes = (appType) => {
    axios
      .get(`/endpoint/connector-types/${appType}`)
      .then((response) => {
        setConnectorTypes(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  const getFtpConnectModes = () => {
    axios
      .get("/endpoint/ftp-connect-modes")
      .then((response) => {
        setFtpConnectModes(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  const getSftpConnectModes = () => {
    axios
      .get("/endpoint/sftp-connect-modes")
      .then((response) => {
        setSftpConnectModes(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  const getConnectTypes = () => {
    axios
      .get("/endpoint/connect-types")
      .then((response) => {
        setConnectTypes(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  const getDownloadFileTypes = () => {
    axios
      .get("/endpoint/download-file-types")
      .then((response) => {
        setDownloadFileTypes(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  const getEmailEndpointAddress = () => {
    axios
      .get("/endpoint/email-endpoint-address")
      .then((response) => {
        setEndpointEmail(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  // Handle dropdown menu app type change
  const handleAppTypeChange = (e) => {
    if (e.target.value !== undefined) {
      setSelectedAppType(e.target.value);
    }
  };

  /**
   * Handle endpoint type dropdown menu change
   * @param {*} e
   */
  const handleProtocolChange = (e) => {
    let value = e.target.value;
    if (value !== undefined) {
      setSelectedProtocol(value);
      setReadOnly(true);
    }
  };

  /**
   * Populate edit endpoint popup with existing endpoint details based on selected endpoint
   */
  const prefillFormValues = () => {
    setSelectedEndpointId(selectedEndpoint.EndpointId ?? -1);
    setEndpointName(selectedEndpoint.EndpointName ?? "");
    setSelectedAppType(selectedEndpoint.AppType ?? "");
    setSelectedProtocol(selectedEndpoint.ProtocolId ?? "");

    let jsonData = JSON.parse(selectedEndpoint.ProtocolParamsJson);
    if (
      selectedEndpoint.ProtocolId === endpointTypesEnum.SFTP ||
      selectedEndpoint.ProtocolId === endpointTypesEnum.FTPS
    ) {
      setFtpInfo({
        hostName: jsonData.HostName,
        userName: jsonData.UserName,
        password: UNCHANGED_PASSWORD,
        downloadPath: jsonData.DownloadPath,
        uploadPath: jsonData.UploadPath,
        downloadFileHandleMethod: jsonData.DownloadFileHandleMethod,
        connectMode: jsonData.ConnectMode,
        port: jsonData.Port === null ? "" : jsonData.Port,
        connectionType: jsonData.ConnectionType,
        mailboxName: jsonData.MailboxName,
        ftpCommands: jsonData.FtpCommands,
        largeFileSize: !jsonData?.LargeFileSize ? 3000 : jsonData.LargeFileSize

      });
    }
    if (selectedEndpoint.ProtocolId === endpointTypesEnum.RESTAPI) {
      setRestApiInfo({
        connectorType:
          jsonData.ConnectorType === -1 ? "" : jsonData.ConnectorType,
        serverName: jsonData.ServerName,
        userName: jsonData.UserName,
        password: UNCHANGED_PASSWORD,
        databaseCode: jsonData.DatabaseCode,
        apiServerName: jsonData.ApiServerName,
        apiCompanyCode: jsonData.ApiCompanyCode,
        apiDatabaseCompanyCode: jsonData.ApiDatabaseCompanyCode,
        apiConsumerKey: UNCHANGED_PASSWORD,
        apiConsumerSecret: UNCHANGED_PASSWORD,
        apiTokenId: UNCHANGED_PASSWORD,
        apiTokenSecret: UNCHANGED_PASSWORD,
      });
    }
    if (selectedEndpoint.ProtocolId === endpointTypesEnum.Email) {
      setEndpointPrompt(jsonData.EndpointPrompt ?? "");
    }
  };

  const checkLargeFileSizeIsInt = () => {
    // large file size must be a number greater than 0
    if (ftpInfo.largeFileSize === "" || ftpInfo.largeFileSize === "undefined") {
      return -1;
    }
    if (/^\d+$/.test(ftpInfo.largeFileSize)) {
      let largeFileSizeToInt = parseInt(ftpInfo.port);
      return largeFileSizeToInt;
    }
    return -1; // Not a valid input/ integer
  };
  const checkPortIsInt = () => {
    const MAX_PORT_VAL = 65535;

    // port can be empty, but must only contain integers, else set to -1 which is invalid and will be flagged by backend
    if (ftpInfo.port === "" || ftpInfo.port === "undefined") {
      return null;
    }
    if (/^\d+$/.test(ftpInfo.port)) {
      let portToInt = parseInt(ftpInfo.port);
      console.log("port:", portToInt, MAX_PORT_VAL);
      if (portToInt > MAX_PORT_VAL) {
        return -2; // Indicate to backend to add error message that port must be valid integer (<65536)
      } else {
        return portToInt;
      }
    }
    return -1; // Not a valid input/ integer
  };

  const getProtocolParams = () => {
    if (
      selectedProtocol === endpointTypesEnum.SFTP ||
      selectedProtocol === endpointTypesEnum.FTPS
    ) {
      return JSON.stringify({
        ...ftpInfo,
        port: checkPortIsInt(),
        largeFileSize: checkLargeFileSizeIsInt(),
      });
    } else if (selectedProtocol === endpointTypesEnum.RESTAPI)
      return JSON.stringify({
        ...restApiInfo,
        connectorType:
          restApiInfo.connectorType === ""
            ? -1
            : parseInt(restApiInfo.connectorType),
      });
    else if (selectedProtocol === endpointTypesEnum.Email)
      return JSON.stringify({
        Email: endpointEmail.Email,
        Password: endpointEmail.Password,
      });
    else return "";
  };
  const params = {
    endpointInfoJson: JSON.stringify({
      EndpointId: selectedEndpointId,
      EndpointName: endpointName,
      AppType: selectedAppType === "" ? -1 : parseInt(selectedAppType), // attempt to parse to integer and if fails, set to null to ensure that it is integer type
      ProtocolId: selectedProtocol === "" ? -1 : parseInt(selectedProtocol),
    }),
    protocolParams: getProtocolParams(),
  };

  const validateParams = createAsyncThunk(
    "endpoint/validate-params",
    async () => {
      const response = await axios.get("/endpoint/validate-params", {
        params: params,
      });
      return response.data;
    },
  );

  /**
   * Update endpoint details upon saving. If successful, displays 'Success' result, clears the values and closes popup upon user acknowledgement
   * If failed to save, displays 'Error' Result and returns to edit popup
   * @returns
   */
  const postEndpointDetails = async () => {
    try {
      const validationMsg = await dispatch(validateParams());

      if (validationMsg.payload && validationMsg.payload !== "") {
        console.error("Error validating:", validationMsg.payload);
        onSavedError(validationMsg.payload);
        return;
      }
    } catch (error) {
      console.error("Error during validation:", error);
      onSavedError("Validation failed due to error");
    }

    // Fetch endpoint data
    axios
      .post("/endpoint", null, {
        params: {
          endpointInfoJson: JSON.stringify({
            EndpointId: selectedEndpointId,
            EndpointName: endpointName,
            AppType: selectedAppType,
            ProtocolId: selectedProtocol,
          }),
          protocolParams: getProtocolParamsJson(),
        },
      })
      .then((response) => {
        if (response.data) {
          handleSave();
          onSavedSuccess("The Endpoint was created / edited successfully!");

          setSelectedEndpoint({});
          clearValues();

          // heap.io
          window.heap.track("Endpoints - Create");
        } else {
          onSavedError("There was an error creating the Endpoint!");
        }
      })
      .catch((error) => {
        console.error("Error", error);
        let errorMessage =
          error.message + " (" + error.response.statusText + ")";
        onSavedError(errorMessage);
      });
  };

  const handleSave = () => {
    setSave(true);
    setEdited(false);
  };

  // Create alerts for save status
  const onSavedSuccess = (message) => {
    dispatch(
      createAlert({
        status: "Success",
        alertType: "result",
        title: "Successfully saved",
        message: message,
        buttonLabel: "Close",
        onClose: props.onCloseDialog,
      }),
    );
  };

  const onSavedError = (message) => {
    dispatch(
      createAlert({
        status: "Error",
        alertType: "result",
        title: "Error Saving",
        message: message,
        buttonLabel: "Ok",
      }),
    );
  };

  const setTitle = (type) => {
    var title = `${type} SFTP`;
    if (selectedProtocol === endpointTypesEnum.SFTP) {
      title = `${type} SFTP`;
    } else if (selectedProtocol === endpointTypesEnum.FTPS) {
      title = `${type} FTPS`;
    } else {
      title = `${type} WebAPI`;
    }
    return title;
  };

  // Creates alerts for connection test
  const onTestSuccess = (message) => {
    var title = setTitle("Connection Test");

    dispatch(
      createAlert({
        status: "Success",
        alertType: "result",
        title: title,
        message: "Connection test was successful!\n" + message,
        buttonLabel: "Close",
      }),
    );
  };

  const onTestError = (message) => {
    var title = setTitle("Connection Test");

    dispatch(
      createAlert({
        status: "Error",
        alertType: "result",
        title: title,
        message: "Connection test failed!\n" + message,
        buttonLabel: "Close",
      }),
    );
  };

  const getProtocolParamsJson = () => {
    if (
      selectedProtocol === endpointTypesEnum.SFTP ||
      selectedProtocol === endpointTypesEnum.FTPS
    ) {
      return JSON.stringify(ftpInfo);
    } else if (selectedProtocol === endpointTypesEnum.RESTAPI) {
      return JSON.stringify(restApiInfo);
    } else if (selectedProtocol === endpointTypesEnum.Email) {
      return JSON.stringify({
        Email: endpointEmail.Email,
        Password: endpointEmail.Password,
        EndpointPrompt: endpointPrompt,
      });
    }
    return "";
  };

  /**
   * Calls appropriate connection/ endpoint test based on endpointType selected. Displays result alert to indicate if test passed or failed
   * @returns
   */
  const testEndpoint = async () => {
    try {
      const validationMsg = await dispatch(validateParams());

      if (validationMsg.payload && validationMsg.payload !== "") {
        console.error("Error validating:", validationMsg.payload);
        onTestError(validationMsg.payload);
        return;
      }
    } catch (error) {
      console.error("Error during validation:", error);
      onTestError("Validation failed due to error");
    }
    axios
      .post("/endpoint/test-endpoint", null, {
        params: {
          endpointInfo: JSON.stringify({
            EndpointId: selectedEndpointId,
            EndpointName: endpointName,
            AppType: selectedAppType,
            ProtocolId: selectedProtocol,
          }),
          protocolParams: getProtocolParamsJson(),
        },
      })
      .then((response) => {
        // Display appropriate alert
        if (response.data.Status) {
          onTestSuccess(response.data.Message);
        } else {
          onTestError(response.data.Message);
        }
      })
      .catch((error) => {
        console.error("Error", error);
        let errorMessage =
          error.message + " (" + error.response.statusText + ")";
        onTestError(errorMessage);
      });
  };

  /**
   * Endpoint type selector component to get user input
   * @returns
   */
  const EndpointType = () => {
    return (
      <>
        <Box sx={{ marginBottom: "20px" }}>
          <Box style={{ display: "inline-flex", flexWrap: "wrap" }}>
            <Box style={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="subheading2"
                className="endpoint-section-header"
              >
                Endpoint Type
              </Typography>
              <FormControl error={requiresUpdate}>
                <InputLabel id="endpoint-type-label">
                  Select Endpoint Type
                </InputLabel>
                <Select
                  labelId="endpoint-type-label"
                  id="endpoint-type-select"
                  value={selectedProtocol}
                  label="Select Endpoint Types"
                  onChange={handleProtocolChange}
                  MenuProps={{
                    style: { zIndex: 1500 },
                  }}
                  className="select-long"
                >
                  {protocols.map((item) => (
                    <MenuItem key={item.Value} value={item.Value}>
                      {item.Label}
                    </MenuItem>
                  ))}
                </Select>
                {requiresUpdate && (
                  <FormHelperText>
                    This Tenant requires an upgrade before using this
                    functionality
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
            <Box style={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="subheading2"
                className="endpoint-section-header"
              >
                App Type
              </Typography>
              <FormControl>
                <InputLabel id="app-type-label">Select App Type</InputLabel>
                <Select
                  labelId="app-type-label"
                  id="app-type-select"
                  value={selectedAppType}
                  label="Select App Types"
                  onChange={handleAppTypeChange}
                  MenuProps={{
                    style: { zIndex: 1500 },
                  }}
                  className="select-long"
                >
                  {appTypes.map((item) => (
                    <MenuItem key={item.Value} value={item.Value}>
                      {item.Label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      </>
    );
  };

  // Handle close button clicked prompting to save changes if needed
  const onCloseClicked = () => {
    const onSavedClick = () => {
      handleSave();
      postEndpointDetails();
      //props.onCloseDialog();
    };

    if (edited && !save) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          title: "Saving",
          message: "Unsaved changes. Do you want to save before closing?",
          buttonLabel: "Close",
          buttonLabel2: "Save",
          onButtonClick: props.onCloseDialog,
          onButtonClick2: onSavedClick,
        }),
      );
    } else {
      props.onCloseDialog();
    }
  };

  //SHEP-360 validation check example
  const handleValidationFormOnchange = (eventTargetValue) => {
    // if eventTargetValue too short then set error to true
    if (eventTargetValue.length == 0) {
      //trigger an error if the length is 0 after loading
      setError(true);
      setHelperText("Name cannot be empty");
    } else {
      setError(false);
      setHelperText("");
    }
    console.log(eventTargetValue.length);
    console.log(error);
  };

  //SHEP-738 generate email for email endpoint
  const GenerateEmail = () => {
    setGenerateEmailDisabled(true);
    axios
      .get("/endpoint/new-email")
      .then((response) => {
        setEndpointEmail(response.data);
      })
      .catch((error) => {
        console.error("Unable to create email: ", error);
      });
  };

  const onCurlError = (message) => {
    var title = setTitle("Generate cURL Command");

    dispatch(
      createAlert({
        status: "Error",
        alertType: "result",
        title: title,
        message: "Generate cURL Command failed!\n" + message,
        buttonLabel: "Close",
      }),
    );
  };

  /**
   * Calls backend to generate cURL command for current endpoint parameters and copy to clipboard.
   */
  const generateCurlAndCopyToClipboard = async () => {
    setCurlLoading(true);
    try {
      // Validate
      const validationMsg = await dispatch(validateParams());
      if (validationMsg.payload) {
        onCurlError(validationMsg.payload);
        return;
      }

      const requestPayload = {
        endpointInfo: JSON.stringify({
          EndpointId: selectedEndpointId,
          EndpointName: endpointName,
          AppType: selectedAppType,
          ProtocolId: selectedProtocol,
        }),
        protocolParams: getProtocolParamsJson(),
      };

      // Create the async data promise
      const curlPromise = axios
        .post("/endpoint/generate-curl", requestPayload)
        .then((response) => {
          if (response.data && response.data.Status) {
            const generatedSnippet = response.data.CurlCommand;
            setCurlCommand(generatedSnippet);
            setShowCurlBox(true);
            return new Blob([generatedSnippet], { type: "text/plain" });
          }
          throw new Error(response.data?.Message || "Failed to generate cURL");
        });

      // Initiate clipboard write IMMEDIATELY (preserves browser user activation)
      if (navigator.clipboard && window.ClipboardItem) {
        const item = new ClipboardItem({ "text/plain": curlPromise });
        await navigator.clipboard.write([item]);
      } else {
        // Fallback for older browsers
        await curlPromise;
      }
    } catch (error) {
      console.error("Error generating cURL:", error);
      onCurlError(error.message || "Failed to generate cURL snippet");
    } finally {
      setCurlLoading(false);
    }
  };

  const isSuperAdmin = userRole === UserRoles.SuperAdmin;
  const isCurlSupported = selectedProtocol === endpointTypesEnum.RESTAPI;

  const handleKeyDown = (event) => {
    if (isNaN(event.key) && event.key !== 'Backspace') {
      event.preventDefault();
    }
  };
  return (
    <Container>
      <Box
        style={{
          margin: "50px 10px",
        }}
      >
        <Box>
          <Box className="popup-section">
            <Typography
              variant="subheading2"
              className="endpoint-section-header"
            >
              Endpoint Name
            </Typography>
            <Box sx={{ marginBottom: "20px" }}>
              <ShepFormField
                style={{ paddingRight: "5px", minWidth: 400 }}
                value={endpointName}
                onChange={(event) => {
                  setEndpointName(event.target.value);
                }}
                error={endpointName.length == 0}
                helperText={
                  endpointName.length == 0 ? "Name cannot be empty" : ""
                }
                id="outlined-basic"
                variant="outlined"
                label="Enter a unique name for this Endpoint"
              />
            </Box>
            <EndpointType />
          </Box>
        </Box>

        {/* Parameters for FTP (SFTP, FTPS) */}
        {(selectedProtocol === endpointTypesEnum.SFTP ||
          selectedProtocol === endpointTypesEnum.FTPS) && (
          <>
            <Box className="popup-section">
              <Typography
                variant="subheading2"
                className="endpoint-section-header"
              >
                Endpoint Parameter
              </Typography>
              <Box style={{ display: "inline-flex", flexWrap: "wrap" }}>
                <Stack direction="row" spacing={20} fullwidth>
                  <Stack direction="column">
                    <Box className="param-box">
                      <Typography variant="body3" className="parameter-label">
                        Mailbox Name*
                      </Typography>
                      <TextField
                        name="mailboxName"
                        placeholder="Enter Mailbox Name"
                        value={ftpInfo.mailboxName}
                        variant="outlined"
                        className="text-field"
                        onChange={handleFtpInfoChange}
                        InputProps={{
                          readOnly: readOnly,
                        }}
                        onFocus={() => setReadOnly(false)}
                      />
                    </Box>
                    <Box className="param-box">
                      <Typography variant="body3" className="parameter-label">
                        Username*
                      </Typography>
                      <TextField
                        name="userName"
                        placeholder="Enter Username"
                        value={ftpInfo.userName}
                        variant="outlined"
                        style={{ minWidth: 400 }}
                        onChange={handleFtpInfoChange}
                        InputProps={{
                          readOnly: readOnly,
                        }}
                        onFocus={() => setReadOnly(false)}
                      />
                    </Box>

                    <Box className="param-box">
                      <ShepTooltip placement="right" title="Path for incoming files to be received by Shep.">
                        <Typography
                          variant="body3"
                          sx={{ 
                            display: "inline-flex",
                            padding: "15px 0px"
                          }}
                        >
                          Inbound Path*
                        </Typography>
                      </ShepTooltip>
                      <TextField
                        name="downloadPath"
                        placeholder="Enter Inbound Path"
                        value={ftpInfo.downloadPath}
                        variant="outlined"
                        style={{ minWidth: 400 }}
                        onChange={handleFtpInfoChange}
                        InputProps={{
                          readOnly: readOnly,
                        }}
                        onFocus={() => setReadOnly(false)}
                      />
                    </Box>

                    <Box className="param-box">
                      <ShepTooltip placement="right" title={
                          <>
                            <div style={{ whiteSpace: "pre-line" }}>
                              Behaviour after inbound files are read
                              <br />
                              Auto: determined by mailbox
                              <br />
                              Archive: file moved to archive folder
                              <br />
                              Delete: permanently delete file after reading
                            </div>
                          </>
                        }>
                        <Typography
                          variant="body3"
                          sx={{ 
                            display: "inline-flex",
                            padding: "15px 0px"
                          }}
                        >
                          Post-Receive Action
                        </Typography>
                      </ShepTooltip>

                      <FormControl>
                        <InputLabel id="download-type-label">
                          Select Inbound Type
                        </InputLabel>
                        <Select
                          name="downloadFileHandleMethod"
                          labelId="download-type-label"
                          id="download-type-select"
                          label="Select Download File Handle Method"
                          value={ftpInfo.downloadFileHandleMethod}
                          onChange={handleFtpInfoChange}
                          style={{ width: 223 }}
                          MenuProps={{
                            style: { zIndex: 1500 },
                          }}
                        >
                          {downloadFileTypes.map((item) => (
                            <MenuItem key={item.Value} value={item.Value}>
                              {item.Label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box className="param-box">
                      <ShepTooltip placement="right" title="Path where outbound documents are placed.">
                        <Typography
                          variant="body3"
                          sx={{ 
                            display: "inline-flex",
                            padding: "15px 0px"
                          }}
                        >
                          Outbound Path*
                        </Typography>
                      </ShepTooltip>

                      <TextField
                        name="uploadPath"
                        placeholder="Enter Outbound Path"
                        value={ftpInfo.uploadPath}
                        variant="outlined"
                        style={{ minWidth: 400 }}
                        onChange={handleFtpInfoChange}
                        InputProps={{
                          readOnly: readOnly,
                        }}
                        onFocus={() => setReadOnly(false)}
                      />
                    </Box>

                    <Box className="param-box">
                    <ShepTooltip placement="right" title="Files over this size will be processed independently.">
                        <Typography
                          variant="body3"
                          sx={{ 
                            display: "inline-flex",
                            padding: "15px 0px"
                          }}
                        >
                          Large File Size Definition (KB)*
                        </Typography>
                      </ShepTooltip>
                      <TextField
                        name="largeFileSize"
                        placeholder="Enter the definition of a large file by size (in KB)"
                        value={ftpInfo.largeFileSize}
                        variant="outlined"
                        className="text-field"
                        style={{}}
                        onChange={handleFtpInfoChange}
                        InputProps={{
                          readOnly: readOnly,
                        }}
                        onFocus={() => setReadOnly(false)}
                      />
                    </Box>
                  </Stack>

                  <Stack direction="column">
                    <Box className="param-box">
                      <Typography variant="body3" className="parameter-label">
                        Hostname*
                      </Typography>
                      <TextField
                        name="hostName"
                        placeholder="Enter Hostname"
                        value={ftpInfo.hostName}
                        variant="outlined"
                        className="text-field"
                        style={{}}
                        onChange={handleFtpInfoChange}
                        InputProps={{
                          readOnly: readOnly,
                        }}
                        onFocus={() => setReadOnly(false)}
                      />
                    </Box>

                    <Box className="param-box">
                      <Typography variant="body3" className="parameter-label">
                        Password
                      </Typography>
                      <TextField
                        name="password"
                        placeholder="********"
                        value={ftpInfo.password}
                        variant="outlined"
                        className="text-field"
                        style={{}}
                        onChange={handleFtpInfoChange}
                        onFocus={() => setReadOnly(false)}
                        type={
                          visiblePasswords["password"] ? "text" : "password"
                        }
                        InputProps={{
                          readOnly: readOnly,
                          // Add the toggle button to the end of the input
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                sx={{
                                  width: "18px",
                                  height: "18px",
                                  padding: "20px",
                                  "& svg": {
                                    width: "18px",
                                    height: "18px",
                                  },
                                }}
                                // Pass the exact field name to the toggle function
                                onClick={() =>
                                  togglePasswordVisibility("password")
                                }
                                onMouseDown={(e) => e.preventDefault()}
                                edge="end"
                              >
                                {visiblePasswords["password"] ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    <Box className="param-box">
                      <ShepTooltip placement="right" title="Port number must be a valid integer (0-65535).">
                        <Typography
                          variant="body3"
                          sx={{ 
                            display: "inline-flex",
                            padding: "15px 0px"
                          }}
                        >
                          Port*
                        </Typography>
                      </ShepTooltip>
                      <TextField
                        name="port"
                        placeholder="Enter Port"
                        value={ftpInfo.port}
                        variant="outlined"
                        className="text-field"
                        style={{}}
                        onChange={handleFtpInfoChange}
                        InputProps={{
                          readOnly: readOnly,
                        }}
                        inputProps={{ maxLength: 5 }} // max length is 5 since port number cannot exceed integer limit (65535)
                        onFocus={() => setReadOnly(false)}
                      />
                    </Box>

                    <Box className="param-box">
                      <Typography variant="body3" className="parameter-label">
                        Connect Mode
                      </Typography>

                      <FormControl>
                        <InputLabel id="connection-mode-label">
                          Select Connection Mode
                        </InputLabel>
                        <Select
                          name="connectMode"
                          labelId="connection-mode-label"
                          id="connection-mode-select"
                          label="Select Connect Mode"
                          value={ftpInfo.connectMode}
                          onChange={handleFtpInfoChange}
                          style={{ width: 223 }}
                          MenuProps={{
                            style: { zIndex: 1500 },
                          }}
                        >
                          {selectedProtocol === 0 &&
                            sftpConnectModes.map((item) => (
                              <MenuItem key={item.Value} value={item.Value}>
                                {item.Label}
                              </MenuItem>
                            ))}
                          {selectedProtocol === 1 &&
                            ftpConnectModes.map((item) => (
                              <MenuItem key={item.Value} value={item.Value}>
                                {item.Label}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box className="param-box">
                      <Typography variant="body3" className="parameter-label">
                        Connection Type
                      </Typography>
                      <FormControl>
                        <InputLabel id="connection-type-label">
                          Select Connection Type
                        </InputLabel>
                        <Select
                          labelId="connection-type-label"
                          id="connection-type-select"
                          name="connectionType"
                          className="select"
                          label="Select Connection Type"
                          value={ftpInfo.connectionType}
                          onChange={handleFtpInfoChange}
                          MenuProps={{
                            style: { zIndex: 1500 },
                          }}
                        >
                          {connectTypes.map((item) => (
                            <MenuItem key={item.Value} value={item.Value}>
                              {item.Label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Stack>
                </Stack>
              </Box>

              <Stack direction="row" spacing={20} fullwidth>
                <Box
                  flex={1}
                  style={{ minHeight: "175px" }}
                  className="param-box"
                >
                  <Typography variant="body3" className="parameter-label">
                    FTP Commands
                  </Typography>
                  <TextField
                    id="outlined-multiline-static"
                    multiline={true}
                    name="ftpCommands"
                    rows={5}
                    label="Enter FTP Commands"
                    value={ftpInfo.ftpCommands}
                    variant="outlined"
                    onChange={handleFtpInfoChange}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        top: -40,
                        minHeight: "125px",
                      },
                      "& .MuiOutlinedInput-root": {
                        top: 40,
                      },
                    }}
                  />
                </Box>
              </Stack>
            </Box>
          </>
        )}

        {/* Parameters for RestAPI*/}
        {selectedProtocol === endpointTypesEnum.RESTAPI && (
          <>
            <Box className="popup-section">
              <Typography
                variant="subheading2"
                className="endpoint-section-header"
              >
                Endpoint Parameter
              </Typography>
              <Stack direction="row" spacing={20}>
                <Stack direction="column">
                  <Box className="param-box">
                    <Typography variant="body3" className="parameter-label">
                      Connector Type*
                    </Typography>
                    <FormControl>
                      <InputLabel id="connector-type-label">
                        Select Connector Type
                      </InputLabel>
                      <Select
                        name="connectorType"
                        labelId="connector-type-label"
                        id="connector-type-select"
                        value={restApiInfo.connectorType}
                        label="Select Connector Type"
                        onChange={handleRestApiInfoChange}
                        MenuProps={{
                          style: { zIndex: 1500 },
                        }}
                        className="select"
                      >
                        {connectorTypes.map((item) => (
                          <MenuItem key={item.Value} value={item.Value}>
                            {item.Label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box className="param-box">
                    <Typography variant="body3" className="parameter-label">
                      Server Name*
                    </Typography>
                    <TextField
                      name="serverName"
                      value={restApiInfo.serverName}
                      variant="outlined"
                      className="text-field"
                      placeholder="Enter Server Name"
                      onChange={handleRestApiInfoChange}
                      InputProps={{
                        readOnly: readOnly,
                      }}
                      onFocus={() => setReadOnly(false)}
                    />
                  </Box>
                  <Box className="param-box">
                    <Typography variant="body3" className="parameter-label">
                      Username*
                    </Typography>
                    <TextField
                      name="userName"
                      value={restApiInfo.userName}
                      variant="outlined"
                      className="text-field"
                      placeholder="Enter Username"
                      onChange={handleRestApiInfoChange}
                      InputProps={{
                        readOnly: readOnly,
                      }}
                      onFocus={() => setReadOnly(false)}
                    />
                  </Box>
                  <Box className="param-box">
                    <Typography variant="body3" className="parameter-label">
                      Password
                    </Typography>
                    <TextField
                      name="password"
                      placeholder="********"
                      value={restApiInfo.password}
                      variant="outlined"
                      className="text-field"
                      onChange={handleRestApiInfoChange}
                      onFocus={() => setReadOnly(false)}
                      type={visiblePasswords["password"] ? "text" : "password"}
                      InputProps={{
                        readOnly: readOnly,
                        // Add the toggle button to the end of the input
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              sx={{
                                width: "18px",
                                height: "18px",
                                padding: "20px",
                                "& svg": {
                                  width: "18px",
                                  height: "18px",
                                },
                              }}
                              // Pass the exact field name to the toggle function
                              onClick={() =>
                                togglePasswordVisibility("password")
                              }
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                            >
                              {visiblePasswords["password"] ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <Box className="param-box">
                    <Typography variant="body3" className="parameter-label">
                      Database Code*
                    </Typography>
                    <TextField
                      name="databaseCode"
                      value={restApiInfo.databaseCode}
                      variant="outlined"
                      className="text-field"
                      placeholder="Enter Database Code"
                      onChange={handleRestApiInfoChange}
                      InputProps={{
                        readOnly: readOnly,
                      }}
                      onFocus={() => setReadOnly(false)}
                    />
                  </Box>
                </Stack>
                <Stack direction="column">
                  <Box className="param-box">
                    <Typography variant="body3" className="parameter-label">
                      Remote API Server Name
                    </Typography>
                    <TextField
                      name="apiServerName"
                      value={restApiInfo.apiServerName}
                      variant="outlined"
                      className="text-field"
                      placeholder="Enter Remote API Server Name"
                      onChange={handleRestApiInfoChange}
                      InputProps={{
                        readOnly: readOnly,
                      }}
                      onFocus={() => setReadOnly(false)}
                    />
                  </Box>
                  <Box className="param-box">
                    <Typography variant="body3" className="parameter-label">
                      Remote API Company Code
                    </Typography>
                    <TextField
                      name="apiCompanyCode"
                      value={restApiInfo.apiCompanyCode}
                      variant="outlined"
                      className="text-field"
                      placeholder="Enter Remote API Company Code"
                      onChange={handleRestApiInfoChange}
                      InputProps={{
                        readOnly: readOnly,
                      }}
                      onFocus={() => setReadOnly(false)}
                    />
                  </Box>
                  <Box className="param-box">
                    <Typography variant="body3" className="parameter-label">
                      Remote API Database Company Code
                    </Typography>
                    <TextField
                      name="apiDatabaseCompanyCode"
                      value={restApiInfo.apiDatabaseCompanyCode}
                      variant="outlined"
                      className="text-field"
                      placeholder="Enter Remote API Database Company Code"
                      onChange={handleRestApiInfoChange}
                      InputProps={{
                        readOnly: readOnly,
                      }}
                      onFocus={() => setReadOnly(false)}
                    />
                  </Box>
                  <Box className="param-box">
                    <Typography variant="body3" className="parameter-label">
                      Remote API Consumer Key
                    </Typography>
                    <TextField
                      name="apiConsumerKey"
                      placeholder="********"
                      value={restApiInfo.apiConsumerKey}
                      variant="outlined"
                      className="text-field"
                      onChange={handleRestApiInfoChange}
                      onFocus={() => setReadOnly(false)}
                      type={
                        visiblePasswords["apiConsumerKey"] ? "text" : "password"
                      }
                      InputProps={{
                        readOnly: readOnly,
                        // Add the toggle button to the end of the input
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              sx={{
                                width: "18px",
                                height: "18px",
                                padding: "20px",
                                "& svg": {
                                  width: "18px",
                                  height: "18px",
                                },
                              }}
                              // Pass the exact field name to the toggle function
                              onClick={() =>
                                togglePasswordVisibility("apiConsumerKey")
                              }
                              onMouseDown={(e) => e.preventDefault()} // Prevents losing focus
                              edge="end"
                            >
                              {visiblePasswords["apiConsumerKey"] ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <Box className="param-box">
                    <Typography variant="body3" className="parameter-label">
                      Remote API Consumer Secret
                    </Typography>
                    <TextField
                      name="apiConsumerSecret"
                      placeholder="********"
                      value={restApiInfo.apiConsumerSecret}
                      variant="outlined"
                      className="text-field"
                      onChange={handleRestApiInfoChange}
                      onFocus={() => setReadOnly(false)}
                      type={
                        visiblePasswords["apiConsumerSecret"]
                          ? "text"
                          : "password"
                      }
                      InputProps={{
                        readOnly: readOnly,
                        // Add the toggle button to the end of the input
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              sx={{
                                width: "18px",
                                height: "18px",
                                padding: "20px",
                                "& svg": {
                                  width: "18px",
                                  height: "18px",
                                },
                              }}
                              // Pass the exact field name to the toggle function
                              onClick={() =>
                                togglePasswordVisibility("apiConsumerSecret")
                              }
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                            >
                              {visiblePasswords["apiConsumerSecret"] ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <Box className="param-box">
                    <Typography variant="body3" className="parameter-label">
                      Remote API Token Id
                    </Typography>
                    <TextField
                      name="apiTokenId"
                      placeholder="********"
                      value={restApiInfo.apiTokenId}
                      variant="outlined"
                      className="text-field"
                      onChange={handleRestApiInfoChange}
                      onFocus={() => setReadOnly(false)}
                      type={
                        visiblePasswords["apiTokenId"] ? "text" : "password"
                      }
                      InputProps={{
                        readOnly: readOnly,
                        // Add the toggle button to the end of the input
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              sx={{
                                width: "18px",
                                height: "18px",
                                padding: "20px",
                                "& svg": {
                                  width: "18px",
                                  height: "18px",
                                },
                              }}
                              // Pass the exact field name to the toggle function
                              onClick={() =>
                                togglePasswordVisibility("apiTokenId")
                              }
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                            >
                              {visiblePasswords["apiTokenId"] ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <Box className="param-box">
                    <Typography variant="body3" className="parameter-label">
                      Remote API Token Secret
                    </Typography>
                    <TextField
                      name="apiTokenSecret"
                      placeholder="********"
                      value={restApiInfo.apiTokenSecret}
                      variant="outlined"
                      className="text-field"
                      onChange={handleRestApiInfoChange}
                      onFocus={() => setReadOnly(false)}
                      type={
                        visiblePasswords["apiTokenSecret"] ? "text" : "password"
                      }
                      InputProps={{
                        readOnly: readOnly,
                        // Add the toggle button to the end of the input
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              sx={{
                                width: "18px",
                                height: "18px",
                                padding: "20px",
                                "& svg": {
                                  width: "18px",
                                  height: "18px",
                                },
                              }}
                              // Pass the exact field name to the toggle function
                              onClick={() =>
                                togglePasswordVisibility("apiTokenSecret")
                              }
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                            >
                              {visiblePasswords["apiTokenSecret"] ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </>
        )}
        {/* Parameters for email - just displaying email to use*/}
        {selectedProtocol === endpointTypesEnum.Email && (
          <>
            <Box className="popup-section">
              <Typography
                variant="subheading2"
                className="endpoint-section-header"
              >
                Endpoint Parameter
              </Typography>

              <Stack direction="column">
                <Box className="param-box">
                  <Typography variant="body3" className="parameter-label">
                    Email:
                  </Typography>
                  <TextField
                    name="email"
                    value={endpointEmail.Email}
                    variant="outlined"
                    className="text-field"
                    error={
                      endpointEmail.Email && (endpointEmail.Email ?? "") === ""
                    }
                    helperText={
                      !endpointEmail.Email ||
                      (endpointEmail?.Email ?? "") !== "" ||
                      userRole === UserRoles.SuperAdmin
                        ? ""
                        : "Please contact your Customer Success Manager to request an Email endpoint."
                    }
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Box>
                {userRole === UserRoles.SuperAdmin && (
                  <ShepButton
                    style={{ marginTop: 8, width: "250px" }}
                    variant={"primary"}
                    disabled={generateEmailDisabled}
                    onClick={GenerateEmail}
                  >
                    Generate Email
                  </ShepButton>
                )}
                <Box
                  sx={{
                    marginBottom: "30px",
                    display: "flex",
                    flexDirection: "column",
                    marginTop: "30px",
                  }}
                >
                  <Box
                    sx={{
                      margin: "30px 0px",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "left",
                      gap: "20px",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body3" className="parameter-label">
                      Endpoint Prompt:
                    </Typography>

                    <ShepTooltip placement="right" title="This prompt will be available to all connections on this Endpoint. You can enable/disable it per connection.">
                      <div>
                        <InfoIcon />
                      </div>
                    </ShepTooltip>
                  </Box>
                  <Box
                    className="user-prompt"
                    sx={{
                      "&> .MuiFormControl-root > .MuiInputBase-root": {
                        height: "auto",
                      },
                      paddingBottom: "40px",
                    }}
                  >
                    <TextField
                      placeholder={"Additional prompt"}
                      value={endpointPrompt}
                      onChange={(event) =>
                        setEndpointPrompt(event.target.value)
                      }
                      maxRows={10}
                      sx={{
                        width: "100%",
                        "&:hover fieldset": {
                          backgroundColor: "#ffffff",
                        },
                        "&.MuiFormControl-root .MuiInputBase-root": {
                          borderRadius: "5px",
                        },
                        "&:hover fieldset": {
                          color: "#000000",
                        },
                      }}
                      multiline
                      inputProps={{ maxLength: 2500 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {endpointPrompt?.length ?? 0}/{2500}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>
          </>
        )}
      </Box>
      {showCurlBox && curlCommand && (
        <Box className="popup-section" style={{ margin: "50px 10px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            Generated cURL Command (Copied to clipboard)
          </label>
          <textarea
            readOnly
            rows={6}
            value={curlCommand}
            style={{
              width: "100%",
              fontFamily: "monospace",
              backgroundColor: "#f5f5f5",
              padding: "10px",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          />
        </Box>
      )}

      {/* Action buttons (close, test, save) */}
      <Box
        style={{
          margin: "10px 10px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box>
          {isSuperAdmin && isCurlSupported && (
            <ShepButton
              disabled={curlLoading}
              onClick={generateCurlAndCopyToClipboard}
            >
              {curlLoading ? "Generating..." : "Copy as cURL Snippet"}
            </ShepButton>
          )}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <ShepButton
            variant="secondary"
            className="second-button"
            onClick={onCloseClicked}
          >
            Close
          </ShepButton>
          <ShepButton
            disabled={requiresUpdate}
            onClick={testEndpoint}
            className="second-button"
          >
            Test
          </ShepButton>
          <ShepButton disabled={requiresUpdate || largeFileSizeHasError} onClick={postEndpointDetails}>
            Save
          </ShepButton>
        </Box>
      </Box>
    </Container>
  );
};

export default NewEndpointSetup;
